import React, { useEffect, useState } from 'react';
import {
    Box, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Alert, useTheme, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton, Tooltip
} from '@mui/material';
import Layout from './Layout';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
const moment = require('moment');

const Contratti = () => {
    const [contratti, setContratti] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [clients, setClients] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [newContratto, setNewContratto] = useState({
        numero_contratto: '',
        data_contratto: '',
        protocollo_contratto: '',
        data_inizio_contratto: '',
        data_scadenza_contratto: '',
        oggetto_contratto: '',
        importo_contratto: '',
        idclientecontratto_fk: '',
        cadenza_fatt: '',
        ccpagamentocontratto_fk: ''
    });
    const theme = useTheme();

    const fetchContratti = () => {
        const token = localStorage.getItem('token');
        fetch('/papi2/contracts', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
            .then(response => {
                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        localStorage.removeItem('token');
                        window.location.href = '/login';
                    }
                    throw new Error('Errore nel caricamento dei contratti');
                }
                return response.json();
            })
            .then(data => {
                if (Array.isArray(data)) {
                    setContratti(data);
                } else {
                    console.error('API non ha restituito un array di contratti:', data);
                    setContratti([]);
                }
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchContratti();
    }, []);

    const fetchInitialData = async () => {
        const token = localStorage.getItem('token');
        try {
            const [clientsRes, banksRes] = await Promise.all([
                fetch('/papi2/masterdata/getClients', { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch('/papi2/masterdata/getBankAccounts', { headers: { 'Authorization': `Bearer ${token}` } })
            ]);
            if (clientsRes.ok) setClients(await clientsRes.json());
            if (banksRes.ok) setBankAccounts(await banksRes.json());
        } catch (err) {
            console.error("Error fetching initial data:", err);
        }
    };

    const handleOpenModal = () => {
        fetchInitialData();
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setNewContratto({
            numero_contratto: '',
            data_contratto: '',
            protocollo_contratto: '',
            data_inizio_contratto: '',
            data_scadenza_contratto: '',
            oggetto_contratto: '',
            importo_contratto: '',
            idclientecontratto_fk: '',
            cadenza_fatt: '',
            ccpagamentocontratto_fk: ''
        });
    };

    const handleSaveContratto = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch('/papi2/contracts', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newContratto)
            });
            if (response.ok) {
                handleCloseModal();
                fetchContratti();
            } else {
                alert("Errore durante il salvataggio del contratto");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteContratto = async (id) => {
        if (!window.confirm("Sei sicuro di voler eliminare questo contratto?")) return;
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`/papi2/contracts/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                fetchContratti();
            } else {
                const msg = await response.text();
                alert(`Errore: ${msg}`);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '';
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return moment(dateString).format('DD/MM/YYYY');
    };

    return (
        <Layout>
            <Box sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                        Gestione Contratti
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenModal}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        Nuovo Contratto
                    </Button>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <TableContainer component={Paper} sx={{ bgcolor: theme.palette.background.paper, boxShadow: 3, borderRadius: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Numero</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Data Contr.</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Protocollo</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Oggetto</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Inizio</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Scadenza</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Importo</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Pagamento</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Cadenza</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Stato</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Azioni</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contratti.map((contratto) => (
                                    <TableRow key={contratto.idcontratto} hover>
                                        <TableCell>{contratto.numero_contratto}</TableCell>
                                        <TableCell>{formatDate(contratto.data_contratto)}</TableCell>
                                        <TableCell>{contratto.protocollo_contratto}</TableCell>
                                        <TableCell>{contratto.nome_cliente}</TableCell>
                                        <TableCell>{contratto.oggetto_contratto}</TableCell>
                                        <TableCell>{formatDate(contratto.data_inizio_contratto)}</TableCell>
                                        <TableCell>{formatDate(contratto.data_scadenza_contratto)}</TableCell>
                                        <TableCell>{formatCurrency(contratto.importo_contratto)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{contratto.nome_banca}</Typography>
                                            <Typography variant="caption" color="text.secondary">{contratto.iban}</Typography>
                                        </TableCell>
                                        <TableCell>{contratto.cadenza_fatt}</TableCell>
                                        <TableCell>
                                            {(() => {
                                                const isExpired = contratto.data_scadenza_contratto && moment(contratto.data_scadenza_contratto).isBefore(moment(), 'day');
                                                const status = isExpired ? "Scaduto" : "Attivo";
                                                return (
                                                    <Chip
                                                        label={status}
                                                        color={status === "Attivo" ? "success" : "error"}
                                                        size="small"
                                                    />
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell align="center">
                                            {(!contratto.num_schede || contratto.num_schede === 0) && (
                                                <Tooltip title="Elimina Contratto">
                                                    <IconButton color="error" size="small" onClick={() => handleDeleteContratto(contratto.idcontratto)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Modal Nuovo Contratto */}
                <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="md">
                    <DialogTitle sx={{ fontWeight: 700 }}>Aggiungi Nuovo Contratto</DialogTitle>
                    <DialogContent>
                        <Box component="form" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box display="flex" gap={2}>
                                <TextField
                                    fullWidth
                                    label="Numero Contratto"
                                    value={newContratto.numero_contratto}
                                    onChange={(e) => setNewContratto({ ...newContratto, numero_contratto: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Data Contratto"
                                    InputLabelProps={{ shrink: true }}
                                    value={newContratto.data_contratto}
                                    onChange={(e) => setNewContratto({ ...newContratto, data_contratto: e.target.value })}
                                />
                            </Box>
                            <Box display="flex" gap={2}>
                                <TextField
                                    fullWidth
                                    label="Protocollo"
                                    value={newContratto.protocollo_contratto}
                                    onChange={(e) => setNewContratto({ ...newContratto, protocollo_contratto: e.target.value })}
                                />
                                <TextField
                                    select
                                    fullWidth
                                    label="Cliente"
                                    value={newContratto.idclientecontratto_fk}
                                    onChange={(e) => setNewContratto({ ...newContratto, idclientecontratto_fk: e.target.value })}
                                >
                                    {clients.map((client) => (
                                        <MenuItem key={client.idcliente} value={client.idcliente}>
                                            {client.ragionesociale}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                            <TextField
                                fullWidth
                                label="Oggetto Contratto"
                                multiline
                                rows={2}
                                value={newContratto.oggetto_contratto}
                                onChange={(e) => setNewContratto({ ...newContratto, oggetto_contratto: e.target.value })}
                            />
                            <Box display="flex" gap={2}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Inizio Contratto"
                                    InputLabelProps={{ shrink: true }}
                                    value={newContratto.data_inizio_contratto}
                                    onChange={(e) => setNewContratto({ ...newContratto, data_inizio_contratto: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Scadenza Contratto"
                                    InputLabelProps={{ shrink: true }}
                                    value={newContratto.data_scadenza_contratto}
                                    onChange={(e) => setNewContratto({ ...newContratto, data_scadenza_contratto: e.target.value })}
                                />
                            </Box>
                            <Box display="flex" gap={2}>
                                <TextField
                                    fullWidth
                                    label="Importo"
                                    type="number"
                                    value={newContratto.importo_contratto}
                                    onChange={(e) => setNewContratto({ ...newContratto, importo_contratto: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    label="Cadenza Fatturazione (mesi)"
                                    type="number"
                                    value={newContratto.cadenza_fatt}
                                    onChange={(e) => setNewContratto({ ...newContratto, cadenza_fatt: e.target.value })}
                                />
                            </Box>
                            <TextField
                                select
                                fullWidth
                                label="Conto Corrente Pagamento"
                                value={newContratto.ccpagamentocontratto_fk}
                                onChange={(e) => setNewContratto({ ...newContratto, ccpagamentocontratto_fk: e.target.value })}
                            >
                                {bankAccounts.map((bank) => (
                                    <MenuItem key={bank.idcc} value={bank.idcc}>
                                        {bank.banca} - {bank.iban}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={handleCloseModal} color="inherit">Annulla</Button>
                        <Button onClick={handleSaveContratto} variant="contained" color="primary">Salva Contratto</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout>
    );
};

export default Contratti;
